import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface NarrativeSide {
  model: string | null;
  public_read: string;
  premier_read: string;
}

interface CatalystPick {
  headline: string;
  url: string;
  source_symbol: string;
  attributed_instrument: string;
  direction_match: boolean;
  reasoning: string;
}

interface CatalystSide {
  model: string | null;
  ran: boolean;
  catalysts: CatalystPick[];
  error: string | null;
}

interface NarrativeSimilarity {
  primary_model: string | null;
  shadow_model: string | null;
  public_chars_primary: number;
  public_chars_shadow: number;
  length_delta_pct: number | null;
  jaccard_first_sentence: number | null;
  jaccard_full_public: number | null;
  jaccard_premier: number | null;
  shadow_error: string | null;
}

interface CatalystSimilarity {
  primary_model: string | null;
  shadow_model: string | null;
  primary_ran: boolean;
  shadow_ran: boolean;
  primary_count: number;
  shadow_count: number;
  shared_headline_count: number;
  shared_instrument_count: number;
  shadow_error: string | null;
}

interface Pair {
  briefingDate: string;
  variant: string;
  generatedAt: string;
  composerVersion: string;
  cost: {
    primary_usd: number;
    shadow_usd: number;
    total_usd: number;
    primary_in: number;
    primary_out: number;
    shadow_in: number;
    shadow_out: number;
  };
  narrative: {
    primary: NarrativeSide;
    shadow: NarrativeSide | null;
    shadow_error: string | null;
    similarity: NarrativeSimilarity | null;
  };
  catalyst: {
    primary: CatalystSide;
    shadow: CatalystSide | null;
    shadow_error: string | null;
    similarity: CatalystSimilarity | null;
  };
}

interface ShadowResponse {
  windowDays: number;
  pairs: Pair[];
  totals: {
    pairs: number;
    primary_cost_usd: number;
    shadow_cost_usd: number;
  };
}

const mutedTextColor = 'rgba(255, 255, 255, 0.5)';
const subduedBackground = 'rgba(255, 255, 255, 0.04)';
const borderColor = 'rgba(255, 255, 255, 0.10)';

function fmtET(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: 'America/New_York',
    }) + ' ET';
  } catch {
    return iso.slice(0, 16);
  }
}

export const AdminBriefingShadow: React.FC = () => {
  const [data, setData] = useState<ShadowResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminService.getBriefingShadow(days);
      setData(d);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) return <div style={{ padding: 20 }}>Loading briefing shadow pairs…</div>;
  if (error) return <div style={{ padding: 20, color: '#f99' }}>Error: {error}</div>;
  if (!data) return null;

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Briefing shadow review</h2>
        <span style={{ color: mutedTextColor, fontSize: 13 }}>
          primary model vs shadow model, last{' '}
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{ background: subduedBackground, color: 'inherit', border: `1px solid ${borderColor}`, padding: '2px 6px' }}
          >
            <option value={3}>3</option>
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={30}>30</option>
          </select>
          {' '}days
        </span>
        <span style={{ color: mutedTextColor, fontSize: 13, marginLeft: 'auto' }}>
          {data.totals.pairs} pairs · primary ${data.totals.primary_cost_usd.toFixed(4)} · shadow ${data.totals.shadow_cost_usd.toFixed(4)}
        </span>
      </div>

      {data.pairs.length === 0 && (
        <div style={{ color: mutedTextColor, padding: 20 }}>
          No shadow pairs in the last {days} days. (Shadow mode may not be enabled, or no deterministic-full briefings have been published.)
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: subduedBackground, textAlign: 'left' }}>
            <th style={{ padding: 8 }}>Date / Variant</th>
            <th style={{ padding: 8 }}>Published (ET)</th>
            <th style={{ padding: 8 }}>Primary model</th>
            <th style={{ padding: 8 }}>Shadow model</th>
            <th style={{ padding: 8, textAlign: 'right' }}>Length Δ</th>
            <th style={{ padding: 8, textAlign: 'right' }}>Lede jacc.</th>
            <th style={{ padding: 8, textAlign: 'right' }}>Public jacc.</th>
            <th style={{ padding: 8, textAlign: 'right' }}>Catalyst overlap</th>
            <th style={{ padding: 8, textAlign: 'right' }}>Cost P/S</th>
            <th style={{ padding: 8 }}></th>
          </tr>
        </thead>
        <tbody>
          {data.pairs.map((p) => {
            const key = `${p.briefingDate}#${p.variant}`;
            const sim = p.narrative.similarity;
            const csim = p.catalyst.similarity;
            const expandedRow = expanded === key;
            return (
              <React.Fragment key={key}>
                <tr style={{ borderTop: `1px solid ${borderColor}` }}>
                  <td style={{ padding: 8 }}>
                    <strong>{p.briefingDate}</strong> {p.variant}
                  </td>
                  <td style={{ padding: 8, color: mutedTextColor }}>{fmtET(p.generatedAt)}</td>
                  <td style={{ padding: 8 }}>{sim?.primary_model || '—'}</td>
                  <td style={{ padding: 8 }}>
                    {sim?.shadow_model || <span style={{ color: '#f99' }}>{sim?.shadow_error || '—'}</span>}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {sim?.length_delta_pct != null ? `${sim.length_delta_pct > 0 ? '+' : ''}${sim.length_delta_pct}%` : '—'}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{sim?.jaccard_first_sentence ?? '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{sim?.jaccard_full_public ?? '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {csim && csim.primary_ran
                      ? `${csim.shared_headline_count}/${Math.max(csim.primary_count, csim.shadow_count)}`
                      : '—'}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right', color: mutedTextColor }}>
                    ${p.cost.primary_usd.toFixed(4)} / ${p.cost.shadow_usd.toFixed(4)}
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      onClick={() => setExpanded(expandedRow ? null : key)}
                      style={{ background: subduedBackground, color: 'inherit', border: `1px solid ${borderColor}`, padding: '4px 10px', cursor: 'pointer' }}
                    >
                      {expandedRow ? 'Hide' : 'Compare'}
                    </button>
                  </td>
                </tr>
                {expandedRow && (
                  <tr style={{ background: subduedBackground }}>
                    <td colSpan={10} style={{ padding: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                          <div style={{ color: mutedTextColor, fontSize: 11, textTransform: 'uppercase', marginBottom: 6 }}>
                            Primary — {sim?.primary_model}
                          </div>
                          <div style={{ marginBottom: 10, lineHeight: 1.5 }}>
                            <strong>Public:</strong> {p.narrative.primary.public_read}
                          </div>
                          <div style={{ lineHeight: 1.5 }}>
                            <strong>Premier:</strong> {p.narrative.primary.premier_read}
                          </div>
                          {p.catalyst.primary.ran && p.catalyst.primary.catalysts.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <strong>Catalysts:</strong>
                              <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                {p.catalyst.primary.catalysts.map((c, i) => (
                                  <li key={i}>
                                    <strong>{c.attributed_instrument}</strong>: {c.headline}
                                    {!c.direction_match && <span style={{ color: '#f99' }}> (dir mismatch)</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ color: mutedTextColor, fontSize: 11, textTransform: 'uppercase', marginBottom: 6 }}>
                            Shadow — {sim?.shadow_model || sim?.shadow_error}
                          </div>
                          {p.narrative.shadow ? (
                            <>
                              <div style={{ marginBottom: 10, lineHeight: 1.5 }}>
                                <strong>Public:</strong> {p.narrative.shadow.public_read}
                              </div>
                              <div style={{ lineHeight: 1.5 }}>
                                <strong>Premier:</strong> {p.narrative.shadow.premier_read}
                              </div>
                              {p.catalyst.shadow && p.catalyst.shadow.ran && p.catalyst.shadow.catalysts.length > 0 && (
                                <div style={{ marginTop: 10 }}>
                                  <strong>Catalysts:</strong>
                                  <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                    {p.catalyst.shadow.catalysts.map((c, i) => (
                                      <li key={i}>
                                        <strong>{c.attributed_instrument}</strong>: {c.headline}
                                        {!c.direction_match && <span style={{ color: '#f99' }}> (dir mismatch)</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ color: '#f99' }}>Shadow failed: {p.narrative.shadow_error}</div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AdminBriefingShadow;
